/* eslint-disable import/group-exports */
import {getVersion, Locale, useConfig, useShopId} from '@aboutyou/config';
import {useLocale} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {useCallback} from 'react';
import {
  useAbTestExperimentsCookieByFeatureFlag,
  useStaleAbTestExperimentsCookie,
} from '../useAbTestExperimentsCookie';
import {useActivatedAbTests} from './useActivatedAbTests';
// eslint-disable-next-line no-restricted-imports, monorepo/no-internal-import
import {useExperimentActivation} from '@aboutyou/tracking/src/trackingHooks/trackExperimentActivation';
import {AvailableFeatureNames} from '../../types';
import {rememberAbTestsActivatedBeforeCookieConsent} from '../abTestsActivatedBeforeCookieConsent';
import {ABTests} from '../types';
import {useGetAbTestUserId} from '../useAbTestUserIdCookie';
import {AB_TESTS_CONFIG_PATH} from './constants';

const OPTIMIZELY_API_URL = 'https://logx.optimizely.com/v1/events';

const OPTIMIZELY_ACCOUNT_ID = '20460546179';

const OPTIMIZELY_ACTIVATE_EVENT = 'campaign_activated';

// These attributes are created manually in Optimizely Dashboard
const OPTIMIZELY_ATTRIBUTES = [
  {
    entity_id: '20801162310',
    key: 'locale',
    type: 'custom',
    value: 'de_DE',
  },
  {
    entity_id: '20802640358',
    key: 'stack_type',
    type: 'custom',
    value: 'mobile',
  },
  {
    entity_id: '20912154828',
    key: 'shop_app_id',
    type: 'custom',
    value: '139',
  },
];

const loggedABTests = new Set<string>();

/**
 * @deprecated Please use the new `useIsFeatureActive` hook with `abTest` config to enable this test
 */
export const useActivateAbTestByFeatureFlagCallback = (
  featureName: AvailableFeatureNames,
) => {
  const abTestExperiments = useAbTestExperimentsCookieByFeatureFlag({
    featureName,
  });
  return useBaseActivateAbTestCallback(abTestExperiments);
};

/**
 * @deprecated Please use the new `useIsFeatureActive` hook with `abTest` config to enable this test
 */
export const useActivateAnyAbTestCallback = () => {
  const abTestExperiments = useStaleAbTestExperimentsCookie();
  return useBaseActivateAbTestCallback(abTestExperiments);
};

const useBaseActivateAbTestCallback = (abTestExperiments: ABTests) => {
  const getAbTestUserId = useGetAbTestUserId();
  const trackExperimentActivation = useExperimentActivation();

  const locale = useLocale();
  const {device} = useConfig();
  const shopId = useShopId().toString();

  const {isExperimentActivated, setTestStateForUserId} = useActivatedAbTests();

  return useCallback(
    async (
      experimentKey: string | undefined,
      config?: {triggerMultipleTimesPerSession?: boolean},
    ) => {
      const abTestUserId = getAbTestUserId();

      // keeps track of abTests that were activated before cookie consent in-memory so that we do not rerender those after cookie consent
      rememberAbTestsActivatedBeforeCookieConsent(experimentKey);

      const experiment = {
        name: '',
        value: '',
      };

      for (const abTestExperiment of abTestExperiments) {
        if (experimentKey === abTestExperiment.key) {
          experiment.name = abTestExperiment.key;
          experiment.value = abTestExperiment.group;
        }
      }

      if (experiment.name.length === 0 || experiment.value.length === 0) {
        return;
      }

      if (
        isExperimentActivated(experiment) &&
        !config?.triggerMultipleTimesPerSession
      ) {
        /**
         * isExperimentActivated checks in `LocalStorage` if the AB Test was already activated for the user, so that we don't activate the AB Test again for the same user.
         * But the attribute we sent to NR Browser needs to happen for returning users as well, in every session. This is why we are storing that in a simple global variable `loggedABTests`.
         */
        if (!loggedABTests.has(experiment.name)) {
          Log.setCustomAttribute(`AB_test_name`, experiment.name);
          Log.setCustomAttribute(`AB_test_value`, experiment.value);
          loggedABTests.add(experiment.name);
        }
        return;
      }

      setTestStateForUserId(experiment, 'pending');

      let decision = {
        campaign_id: '',
        experiment_id: '',
        variation_id: '',
      };

      try {
        const res = await fetch(
          `${AB_TESTS_CONFIG_PATH}/${experiment.name}/${experiment.value}`,
        );
        decision = await res.json();
      } catch (e) {
        Log.warn('@aboutyou/feature-flags', `No decisions`, {
          optimizelyApiUrl: OPTIMIZELY_API_URL,
          userId: abTestUserId,
          abTests: {
            runningAbTests: {
              name: experiment.name,
              variant: experiment.value,
            },
          },
          error: e,
        });
      }

      if (!decision.campaign_id.length) {
        return Log.warn(
          '@aboutyou/feature-flags',
          'No decision campaign id obtained, no experiment activation',
          {
            optimizelyApiUrl: OPTIMIZELY_API_URL,
            userId: abTestUserId,
            abTests: {
              runningAbTests: {
                name: experiment.name,
                variant: experiment.value,
              },
            },
          },
        );
      }

      try {
        // this functionality will go to tracking library so axios is used here directly
        // to avoid the loose coupling
        await fetch(OPTIMIZELY_API_URL, {
          method: 'POST',
          mode: 'cors',
          headers: {'Content-Type': 'application/json'},
          body: getOptimizelyTrackingRequestBody(
            abTestUserId,
            decision,
            locale,
            device,
            shopId,
          ),
        });

        setTestStateForUserId(experiment, 'activated');
        trackExperimentActivation(experiment);
        Log.setCustomAttribute(`AB_test_name`, experiment.name);
        Log.setCustomAttribute(`AB_test_value`, experiment.value);
        loggedABTests.add(experiment.name);
      } catch (e) {
        Log.error(
          '@aboutyou/tracking',
          `Error while tracking optimizely custom event. Optimizely API request failed`,
          {
            optimizelyApiUrl: OPTIMIZELY_API_URL,
            userId: abTestUserId,
            abTests: {
              runningAbTests: {
                name: experiment.name,
                variant: experiment.value,
              },
            },
            error: e,
          },
        );
      }
    },
    [
      getAbTestUserId,
      abTestExperiments,
      locale,
      device,
      shopId,
      isExperimentActivated,
      setTestStateForUserId,
      trackExperimentActivation,
    ],
  );
};

const getOptimizelyTrackingRequestBody = (
  abTestUserId: string,
  decision: {
    campaign_id: string;
    experiment_id: string;
    variation_id: string;
  },
  locale: Locale,
  device: 'mobile' | 'desktop',
  shopId: string,
) => {
  return JSON.stringify({
    account_id: OPTIMIZELY_ACCOUNT_ID,
    visitors: [
      {
        visitor_id: abTestUserId,
        // attributes are already defined when decision for same user is made in middleware
        attributes: OPTIMIZELY_ATTRIBUTES.map(attribute => {
          if (attribute.key === 'locale') {
            attribute.value = locale;
          }
          if (attribute.key === 'stack_type') {
            attribute.value = device;
          }
          if (attribute.key === 'shop_app_id') {
            attribute.value = shopId;
          }
          return attribute;
        }),
        snapshots: [
          {
            decisions: [decision],
            events: [
              {
                entity_id: decision.campaign_id,
                type: OPTIMIZELY_ACTIVATE_EVENT,
                timestamp: Date.now(),
                uuid: Date.now().toString(),
              },
            ],
          },
        ],
      },
    ],
    anonymize_ip: true,
    client_name: 'tdr',
    client_version: process.env.NODE_ENV !== 'test' ? getVersion() : '0',
    enrich_decisions: true,
  });
};
